import type { MissionAssessmentTechnologyUseCreateDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook';
import { useGetDomains } from '@queries/domain.query';
import { useGetUses } from '@queries/use.query';
import {
  missionAssessmentTechnologyUseCreateSchema,
  missionAssessmentTechnologyUseDomainCreateSchema,
} from '@schemas/mission-assessment.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { mapToSelectOptions } from '@utils/select.util';
import { PlusIcon, TrashIcon } from 'lucide-react';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { z } from 'zod/v4';

type MissionAssessmentFormRecommendedTechUsesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MissionAssessmentTechnologyUseCreateDto) => void;
  techUseToEdit?: MissionAssessmentTechnologyUseCreateDto | null;
};

const getDefaultValues = (
  techUseToEdit?: MissionAssessmentTechnologyUseCreateDto | null,
): MissionAssessmentTechnologyUseCreateDto => {
  if (techUseToEdit) {
    return {
      description: techUseToEdit.description ?? '',
      domains: techUseToEdit.domains?.length ? techUseToEdit.domains : [{ domainId: '' }],
      useId: techUseToEdit.useId ?? '',
    };
  }

  return {
    description: '',
    domains: [{ domainId: '' }],
    useId: '',
  };
};

export const MissionAssessmentFormRecommendedTechUsesModal: FunctionComponent<
  MissionAssessmentFormRecommendedTechUsesModalProps
> = (props) => {
  const { isOpen, onClose, onSubmit, techUseToEdit } = props;

  const { data: availableUses, isLoading: isLoadingUses } = useGetUses();
  const { data: availableDomains, isLoading: isLoadingDomains } = useGetDomains();

  const useOptions = useMemo(() => mapToSelectOptions(availableUses ?? []), [availableUses]);
  const domainOptions = useMemo(() => mapToSelectOptions(availableDomains ?? []), [availableDomains]);

  const form = useAppForm({
    defaultValues: getDefaultValues(techUseToEdit),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      try {
        const validationResult = missionAssessmentTechnologyUseCreateSchema.safeParse(value);

        if (!validationResult.success) {
          const message = validationResult.error.issues[0]?.message;
          errorToast({
            description: message || 'Please fill out all required fields correctly.',
          });
          return;
        }

        onSubmit(validationResult.data);
      } catch (error) {
        console.error('Error in form submission:', error);
        errorToast({ description: 'An error occurred while submitting the form.' });
      }
    },
    validators: {
      onSubmit: missionAssessmentTechnologyUseCreateSchema,
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  return (
    <Modal
      centered
      footer={{
        leftButton: {
          onClick: onClose,
          children: 'Cancel',
          variant: 'secondary',
        },
        rightButton: {
          onClick: () => {
            form.validateAllFields('submit');
            form.handleSubmit();
          },
          children: techUseToEdit ? 'Save' : 'Add',
        },
      }}
      header={{ title: techUseToEdit ? 'Edit Technology Use' : 'Add Technology Use' }}
      isOpen={isOpen}
      onClose={onClose}
      width="620px"
    >
      <form.AppForm>
        <div className="space-y-4 text-sm">
          <form.AppField
            name="useId"
            validators={{
              onChange: missionAssessmentTechnologyUseCreateSchema.shape.useId,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                disabled={isLoadingUses}
                emptyMessage="No uses found."
                formatValue={(option) => option?.label || 'Select use'}
                label="Use"
                options={useOptions}
                placeholder="Select use"
                required
                searchPlaceholder="Search uses..."
              />
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.TextAreaField
                label="Description"
                placeholder="Optional description for this technology use..."
                rows={3}
              />
            )}
          </form.AppField>

          <form.AppField
            mode="array"
            name="domains"
            validators={{
              onChange: z.array(missionAssessmentTechnologyUseDomainCreateSchema).min(1, {
                message: 'At least one domain must be selected',
              }),
            }}
          >
            {(domainsField) => (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">
                  Domains <span className="text-red-500">*</span>
                </div>
                <div className="space-y-2 rounded-md border border-gray-200 p-3">
                  {domainsField.state.value?.map((_, domainIndex) => (
                    <div
                      className="flex items-start gap-2"
                      key={domainIndex}
                    >
                      <div className="grow">
                        <form.AppField
                          name={`domains[${domainIndex}].domainId`}
                          validators={{
                            onChange: missionAssessmentTechnologyUseDomainCreateSchema.shape.domainId,
                          }}
                        >
                          {(domainField) => (
                            <domainField.SelectPopoverField
                              disabled={isLoadingDomains}
                              emptyMessage="No domains found."
                              formatValue={(option) => option?.label || 'Select domain'}
                              options={domainOptions}
                              placeholder="Select domain"
                              searchPlaceholder="Search domains..."
                            />
                          )}
                        </form.AppField>
                      </div>

                      {(domainsField.state.value?.length ?? 0) > 1 && (
                        <IconButton
                          ariaLabel="Remove domain"
                          icon={<TrashIcon size={16} />}
                          onClick={() => domainsField.removeValue(domainIndex)}
                          size="lg"
                          variant="secondary"
                        />
                      )}
                    </div>
                  ))}

                  <Button
                    className="h-auto self-start"
                    onClick={() => domainsField.pushValue({ domainId: '' })}
                    type="button"
                    variant="link"
                  >
                    <PlusIcon className="size-4" />
                    Add Domain
                  </Button>
                </div>
              </div>
            )}
          </form.AppField>
        </div>
      </form.AppForm>
    </Modal>
  );
};
